import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Delete } from '@mui/icons-material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Skeleton,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useEffect } from 'react'

function SortableQueueItem({
  id,
  title,
  thumbnail_url,
  index,
  videoId,
  onPlay,
  onDelete,
  isMobile,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const isActive = id === videoId

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: isActive ? 'green' : 'transparent',
        borderRadius: 1,
        mb: 1,
        cursor: isActive ? 'not-allowed' : 'pointer',
        pointerEvents: isActive ? 'not-allowed' : 'auto',
        '&:hover': {
          bgcolor: isActive ? 'green' : '#1e1e1e',
        },
      }}
      onClick={() => {
        if (!isActive) onPlay()
      }}
    >
      {/* Drag handle + chip + title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
          // cursor: 'grab',
        }}
      >
        <DragIndicatorIcon
          {...listeners}
          {...attributes}
          sx={{ color: isActive ? 'white' : 'gray', cursor: 'grab' }}
        />
        <Chip
          label={index + 1}
          size='small'
          color='success'
          sx={{
            color: isActive ? 'green' : 'white',
            background: isActive ? 'white' : 'green',
            mr: 1,
          }}
        />
        <Typography
          variant='body1'
          sx={{
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'white' : 'white',
          }}
        >
          {title}
        </Typography>
      </Box>

      <ListItemSecondaryAction>
        <IconButton
          edge='end'
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          color='error'
          size='small'
        >
          {!isActive && <Delete />}
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  )
}

export default function DraggableQueue({
  queue,
  videoId,
  setQueue,
  handlePlayQueueItem,
  handleDeleteVideo,
  socket,
  token,
  isMobile,
  roomId,
}) {
  const sensors = useSensors(useSensor(PointerSensor))
  const [showSkeleton, setShowSkeleton] = useState(true)

  useEffect(() => {
    if (queue.length === 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 2500)
      return () => clearTimeout(timer)
    }
  }, [queue])

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = queue.findIndex((q) => String(q.video_id) === active.id)
    const newIndex = queue.findIndex((q) => String(q.video_id) === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(queue, oldIndex, newIndex)
    setQueue(reordered)
    socket.emit('updateQueue', { newQueue: reordered, token, roomId })
  }
  if (queue.length === 0) {
    return showSkeleton ? (
      
      [1, 2].map((e) => (
        <Box sx={{ width: '95%', margin: 'auto' }}>
          <Skeleton animation='pulse' height={50} sx={{ bgcolor: '#434343' }} />
          <Skeleton
            animation='pulse'
            height={50}
            sx={{ mt: 1, bgcolor: '#434343' }}
          />
          <Skeleton
            animation='pulse'
            height={50}
            sx={{ mt: 1, bgcolor: '#434343' }}
          />
        </Box>
      ))

    ) : (
      <Typography sx={{ textAlign: 'center', color: 'gray', mt: 2 }}>
        This room's queue is currently empty. Add a song to get started!
      </Typography>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={queue.map((song) => String(song.video_id))}
        strategy={verticalListSortingStrategy}
      >
        <List>
          {queue?.length > 0 &&
            queue.map((song, index) => (
              <SortableQueueItem
                key={song.video_id}
                id={String(song.video_id)}
                title={song.title}
                thumbnail_url={song.thumbnail}
                index={index}
                videoId={videoId}
                onPlay={() => handlePlayQueueItem(song)}
                onDelete={() => handleDeleteVideo(song.video_id)}
                isMobile={isMobile}
              />
            ))}
        </List>
      </SortableContext>
    </DndContext>
  )
}
